import React, { useEffect, useRef, useState } from "react";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Upload,
  X,
  CheckCircle2,
  Check,
  AlertTriangle,
  ImagePlusIcon,
} from "lucide-react";
import {
  createBranch,
  createBranchMethod,
  getAllBranch,
  getBranchById,
  updateBranch,
} from "../../../services/admin/branch-service";
import { showErrorToast, showSuccessToast } from "../../../utils/toast-utils";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { useQuery } from "@tanstack/react-query";
import Loader from "../../../components/loader/loader";
import Table2 from "../../../components/table/table-2";
import { getFieldError } from "../../../utils/validators";
import isEmail from "validator/lib/isEmail";
import { isNumeric } from "validator";
import { useGetAllBranches } from "../../../hook/admin/use-branch";
import ViewBranchDataByID from "../../../components/viewsModals/viewBranchDataByID";
import FormHeading from "../../../components/branch/formHeading";

const CreateBranch = () => {
  const [form, setForm] = useState({
    hospital_name: "",
    hospital_email: "",
    hospital_phone: "",
    hospital_full_address: "",
    file: null,
  });

  const [response, setResponse] = useState(null);
  const [updateResponse, setUpdateResponse] = useState(null);

  const [preview, setPreview] = useState(null);
  const [createBranchLoading, setCreateBranchLoading] = useState(false);
  const [updateBranchLoading, setUpdateBranchLoading] = useState(false);
  const [closeMessage, setCloseMessage] = useState(false);
  const [inputError, setInputError] = useState("");
  const [viewBranchLoading, setViewBranchLoading] = useState(false);
  const [viewBranchData, setViewBranchdata] = useState(null);
  const [formType, setFormType] = useState("create");
  const [singleBranchData, setSingleBranchData] = useState(null);
  const [editClicked, setEditClicked] = useState(false);
  const fileInputRef = useRef(null);
  const [branchId, setBranchId] = useState(null);

  const branchColumns = [
    { label: "Hospital Name", key: "hospitalName" },
    { label: "Hospital ID", key: "hospitalId" },
    { label: "Email", key: "hospitalEmail" },
    { label: "Phone", key: "hospitalPhone" },
    { label: "Address", key: "hospitalFullAddress" },

    {
      label: "Status",
      key: "isActive",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.isActive === 1
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {row.isActive === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },

    {
      label: "Actions",
      key: "actions",
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleView(row.id)}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
          >
            View
          </button>

          <button
            onClick={() => handleEditClick(row.id)}
            className="px-2 py-1 text-xs bg-yellow-400 text-black rounded hover:bg-yellow-200"
          >
            Edit
          </button>
        </div>
      ),
    },

    {
      label: "Created",
      key: "created_at",
      render: (row) => new Date(row.created_at).toLocaleDateString(),
    },
  ];

  const handleView = async (id) => {
    if (!id) return;

    setViewBranchLoading(true);

    try {
      const res = await getBranchById({ branch_id: id });
      console.log(res);
      setViewBranchdata(res);
    } catch (error) {
      console.log(error);
    } finally {
      setViewBranchLoading(false);
    }
  };

  const handleEditClick = async (id) => {
    if (!id) return;

    setViewBranchLoading(true);
    setEditClicked(true);
    setResponse(null);
    setUpdateResponse(null);

    try {
      setFormType("update");

      const res = await getBranchById({ branch_id: id });

      if (res?.success && res?.data?.status === 200) {
        const branch = res.data.data;

        setForm((prev) => ({
          ...prev,
          hospital_name: branch.hospitalName ?? "",
          hospital_email: branch.hospitalEmail ?? "",
          hospital_phone: branch.hospitalPhone ?? "",
          hospital_full_address: branch.hospitalFullAddress ?? "",
        }));

        setBranchId(branch.id);

        if (branch.imageUrl) {
          setPreview(branch.imageUrl);
        }

        console.log(branch);
      }

      setSingleBranchData(res);
    } catch (error) {
      console.log(error);
    } finally {
      setViewBranchLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size < 5 * 1024 * 1024) {
      setForm((prev) => ({ ...prev, file }));
      setPreview(URL.createObjectURL(file));
    } else {
      alert("File size should be less than 5MB");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setUpdateResponse(null);

    if (form?.hospital_email !== "" || form?.hospital_phone !== "") {
      const validateEmail = isEmail(form?.hospital_email);
      const validateMobile = isNumeric(form?.hospital_phone);

      if (!validateEmail) {
        return setInputError("enter valid email");
      } else if (!validateMobile) {
        return setInputError("enter valid mobile No.");
      } else if (form?.hospital_phone.length < 10) {
        return setInputError("enter 10 digits in phone no.");
      }
    }

    const formData = new FormData();

    if (form.hospital_name)
      formData.append("hospital_name", form.hospital_name);
    if (form.hospital_email)
      formData.append("hospital_email", form.hospital_email);
    if (form.hospital_phone)
      formData.append("hospital_phone", form.hospital_phone);
    if (form.hospital_full_address)
      formData.append("hospital_full_address", form.hospital_full_address);
    if (form.file) formData.append("file", form.file);

    console.log("Submitting Branch Data:", form);
    setCreateBranchLoading(true);
    setInputError("");

    try {
      const res = await createBranch(formData);

      if (res) {
        console.log(res);
        setResponse(res);
      }

      if (res?.status === 201 || res?.data?.status === 201) {
        setForm({
          hospital_email: "",
          hospital_full_address: "",
          hospital_name: "",
          hospital_phone: "",
        });
        setPreview(null);

        showSuccessToast(
          res?.data?.message || res?.message || "Branch creation successfull",
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setCreateBranchLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setResponse(null);

    if (form?.hospital_email !== "" || form?.hospital_phone !== "") {
      const validateEmail = isEmail(form?.hospital_email);
      const validateMobile = isNumeric(form?.hospital_phone);

      if (!validateEmail) {
        return setInputError("enter valid email");
      } else if (!validateMobile) {
        return setInputError("enter valid mobile No.");
      } else if (form?.hospital_phone.length < 10) {
        return setInputError("enter 10 digits in phone no.");
      }
    }

    const formData = new FormData();

    if (form.hospital_name)
      formData.append("hospital_name", form.hospital_name);
    if (form.hospital_email)
      formData.append("hospital_email", form.hospital_email);
    if (form.hospital_phone)
      formData.append("hospital_phone", form.hospital_phone);
    if (form.hospital_full_address)
      formData.append("hospital_full_address", form.hospital_full_address);
    if (form.file) formData.append("file", form.file);
    if (branchId) formData.append("id", branchId);

    console.log("Submitting Branch Data:", form);
    setUpdateBranchLoading(true);
    setInputError("");

    try {
      const res = await updateBranch(formData);

      if (res) {
        console.log(res);
        setUpdateResponse(res);
      }

      if (res?.status === 200 || res?.data?.status === 200) {
        setForm({
          hospital_email: "",
          hospital_full_address: "",
          hospital_name: "",
          hospital_phone: "",
        });
        setPreview(null);
        showSuccessToast(
          res?.message || res?.data?.message || "Branch Updated successfully",
        );
      } else {
        showErrorToast("something went wrong");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setUpdateBranchLoading(false);
    }
  };

  const { data: branches = [], isLoading: branchLoading } = useGetAllBranches();

  useEffect(() => {
    if (!editClicked) return;

    const element = document.getElementById("scrollForEdit");

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    const timeout = setTimeout(() => {
      setEditClicked(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [editClicked]);

  return (
    <div className="min-h-screen">
      {(branchLoading ||
        createBranchLoading ||
        viewBranchLoading ||
        updateBranchLoading) && <Loader />}
      <div className="">
        {/* this is for scrolling to top when user want to edit details */}
        <div id="scrollForEdit" />
        {/* Header */}
        <div className="mb-4">
          <FormHeading
            subtitle={
              formType === "create"
                ? "Add a new hospital branch to your system"
                : "Update branch information and settings"
            }
            title={formType === "create" ? "Create New Branch" : "Edit Branch"}
            formType={formType}
          />
        </div>

        <form
          onSubmit={formType === "create" ? handleSubmit : handleUpdate}
          className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="px-6 py-2">
            {/* Section 1: Basic Information */}
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Hospital Name <span className="text-red-700">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 size-5" />
                    <input
                      required
                      name="hospital_name"
                      value={form.hospital_name}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      placeholder="e.g. City Care North"
                    />
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 size-5" />
                        <input
                          type="email"
                          value={form.hospital_email}
                          name="hospital_email"
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                          placeholder="info@citycare.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Phone Number
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 size-5" />
                        <input
                          type="tel"
                          value={form.hospital_phone}
                          name="hospital_phone"
                          maxLength={10}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                          placeholder="9876543210"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: address Information */}
            <div className="md:col-span-2 pt-4 space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Full Address *
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 text-slate-400 size-5" />
                <textarea
                  required
                  value={form.hospital_full_address}
                  name="hospital_full_address"
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Enter the complete street address..."
                ></textarea>
              </div>
            </div>

            {/* Section 3: Branding */}
            <div className="w-full bg-white rounded-xl  shadow-sm p-4">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                {/* Left - Title */}
                <div className="flex items-center gap-4 min-w-fit">
                  <h2 className="text-lg font-semibold text-slate-800 whitespace-nowrap">
                    Branch Logo
                  </h2>

                  {preview && (
                    <button
                      type="button"
                      onClick={() => setPreview(null)}
                      className="flex items-center gap-2 text-xs bg-green-50 text-green-700 px-3 py-1 rounded-lg hover:bg-green-100 transition"
                    >
                      <ImagePlusIcon size={16} />
                      Add New
                    </button>
                  )}

                  {/* Center - Small Upload / Preview */}
                  <div className="flex items-center justify-center">
                    {preview ? (
                      <div className="relative w-20 h-20 rounded-lg overflow-hidden group border">
                        <img
                          src={preview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setPreview(null)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition"
                        >
                          <X className="size-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center w-24 h-20 border border-dashed border-slate-300 rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 transition text-xs text-slate-500">
                        <Upload className="size-4 mr-1" />
                        Upload
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Right - Actions */}
                <div className="flex items-center gap-3 min-w-fit">
                  {/* <button
                    type="button"
                    className="px-4 py-2.5 text-sm text-slate-600 hover:text-slate-800 transition"
                  >
                    Cancel
                  </button> */}

                  <button
                    type="submit"
                    className="px-4 py-2.5 bg-[#0A1F44] hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm flex items-center gap-2 transition active:scale-95"
                  >
                    <CheckCircle2 className="size-4" />
                    {formType === "create" ? "Create" : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {inputError && (
            <div className="text-red-600 w-full flex items-center my-2 gap-2 animate-bounce justify-center">
              <AlertTriangle />
              {inputError}
            </div>
          )}

          {/* response message */}

          {!closeMessage &&
            (response?.data?.status === 201 ||
              updateResponse?.data?.status === 200) && (
              <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 shadow-sm animate-fade-in">
                <div className="flex justify-center items-center gap-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>

                  <div className="flex flex-col">
                    <p className="text-sm font-semibold text-green-700">
                      Success
                    </p>
                    <p className="text-sm text-green-600">
                      {response?.message ||
                        response?.data?.message ||
                        updateResponse?.data?.message ||
                        "Branch created/updated successfully"}
                    </p>
                  </div>
                </div>

                <button onClick={() => setCloseMessage(true)} className="">
                  <X />
                </button>
              </div>
            )}
        </form>

        {viewBranchData && (
          <ViewBranchDataByID
            viewBranchData={viewBranchData?.data?.data}
            setViewBranchData={setViewBranchdata}
          />
        )}

        <Card className="rounded-xl border-none mt-4 bg-white ">
          <CardHeader>
            <CardTitle className="text-md md:text-xl text-gray-600">
              All Branches
            </CardTitle>
          </CardHeader>
          <CardContent className="pl-4 text-[#081a3a] text-2xl md:text-4xl font-bold">
            <Table2
              headerBg="bg-[#0A1F44]"
              columns={branchColumns}
              data={branches}
              rowsPerPage={4}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateBranch;
